import { useState } from "react";
import { api } from "../lib/api";
import { getAttribution } from "../lib/attribution";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Mail, Phone, MapPin, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source: "contact", ...getAttribution() });
      toast.success("Message received! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-24 lg:py-32 bg-[#EFEAE0]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 lg:gap-12">
        <Reveal className="lg:col-span-5">
          <p className="text-sm sm:text-base font-bold uppercase tracking-[0.18em] text-[#FF008F] mb-4">Get in Touch</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] leading-tight">
            Talk to a real person, today.
          </h2>
          <p className="mt-5 text-[#475569]">
            Have a question before filling out the claim form? Drop us a note or call directly — there's no pressure and no fees to chat.
          </p>

          <div className="mt-10 space-y-5">
            <InfoRow icon={<Phone className="w-5 h-5" />} label="Phone" value="1-833-535-9505" href="tel:+18335359505" />
            <InfoRow icon={<Mail className="w-5 h-5" />} label="Email" value="recover@americanassetrecoveryagency.com" href="mailto:recover@americanassetrecoveryagency.com" valueClass="text-[13px] sm:text-sm whitespace-nowrap" />
            <InfoRow icon={<MapPin className="w-5 h-5" />} label="Location" value="Based in Southwest Florida — serving all 48 states" />
            <InfoRow icon={<Clock className="w-5 h-5" />} label="Hours" value="Mon–Fri, 9am – 4pm ET" />
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
        <form onSubmit={submit} className="bg-white rounded-3xl border border-[#E2DDD3] shadow-sm p-6 sm:p-8 lg:p-10" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Name *</Label>
              <Input value={form.name} onChange={set("name")} className="mt-1.5 bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="contact-name" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Phone *</Label>
              <Input value={form.phone} onChange={set("phone")} type="tel" className="mt-1.5 bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="contact-phone" required />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Email *</Label>
              <Input value={form.email} onChange={set("email")} type="email" className="mt-1.5 bg-[#F7F5F0] border-[#E2DDD3] h-11" data-testid="contact-email" required />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-[#475569]">Message</Label>
              <Textarea value={form.message} onChange={set("message")} className="mt-1.5 bg-[#F7F5F0] border-[#E2DDD3] min-h-32" data-testid="contact-message" />
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="mt-8 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white rounded-full h-12 text-lg font-bold transition-all duration-300 hover:-translate-y-0.5" data-testid="contact-submit">
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : "Send Message"}
          </Button>
        </form>
        </Reveal>
      </div>
    </section>
  );
}

function InfoRow({ icon, label, value, href, valueClass = "" }) {
  const baseClass = `mt-0.5 break-words ${valueClass || "text-base"}`;
  const valueEl = href
    ? <a href={href} className={`${baseClass} text-[#0F172A] hover:text-[#FF008F]`}>{value}</a>
    : <p className={`${baseClass} text-[#0F172A]`}>{value}</p>;
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-white border border-[#E2DDD3] grid place-items-center text-[#1E3A8A] shrink-0">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-[#64748B]">{label}</p>
        {valueEl}
      </div>
    </div>
  );
}
